import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Drawer,
  IconButton,
  Divider
} from '@mui/material';
import {
  CalendarBlank,
  Heart,
  Users,
  Storefront,
  Clock,
  MusicNote,
  Palette,
  UserCircle,
  X,
  ArrowLeft
} from '@phosphor-icons/react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import useThemeStyles from '../../../theme/useThemeStyles';
import OverviewSection from './sections/OverviewSection';
import CoupleSection from './sections/CoupleSection';
import GuestListSection from './sections/GuestListSection';
import SuppliersSection from './sections/SuppliersSection';
import TimelineSection from './sections/TimelineSection';
import MusicSection from './sections/MusicSection';
import StyleSection from './sections/StyleSection';
import WeddingPartySection from './sections/WeddingPartySection';

const SECTIONS = [
  { id: 'overview', label: 'Overview', icon: CalendarBlank },
  { id: 'couple', label: 'Couple Information', icon: Heart },
  { id: 'guests', label: 'Guest List', icon: Users },
  { id: 'suppliers', label: 'Suppliers', icon: Storefront },
  { id: 'timeline', label: 'Timeline', icon: Clock },
  { id: 'music', label: 'Music', icon: MusicNote },
  { id: 'style', label: 'Style & Theme', icon: Palette },
  { id: 'wedding-party', label: 'Wedding Party', icon: UserCircle }
];

const WeddingDetailsMasterPage = () => {
  const { weddingId } = useParams();
  const navigate = useNavigate();
  const styles = useThemeStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [activeSection, setActiveSection] = useState('overview');
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [weddingData, setWeddingData] = useState(null);
  const [isRedacted, setIsRedacted] = useState(false);

  useEffect(() => {
    fetchWeddingDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [weddingId]);

  const fetchWeddingDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/api/supplier/wedding/${weddingId}/details`);
      setWeddingData(response.data);
      setIsRedacted(response.data.isRedacted || false);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You do not have access to this wedding');
      } else if (err.response?.status === 404) {
        setError('Wedding not found');
      } else {
        setError('Failed to load wedding details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const renderContent = () => {
    if (!weddingData) return null;

    switch (activeSection) {
      case 'overview':
        return <OverviewSection weddingData={weddingData} />;
      case 'couple':
        return <CoupleSection partners={weddingData.partners} />;
      case 'guests':
        return <GuestListSection guests={weddingData.guests} weddingData={weddingData} />;
      case 'suppliers':
        return <SuppliersSection suppliers={weddingData.suppliers} weddingId={weddingId} />;
      case 'timeline':
        return <TimelineSection timeline={weddingData.timeline} />;
      case 'music':
        return <MusicSection music={weddingData.music} />;
      case 'style':
        return <StyleSection style={weddingData.details?.style} />;
      case 'wedding-party':
        return <WeddingPartySection weddingParty={weddingData.weddingParty} />;
      default:
        return <OverviewSection weddingData={weddingData} />;
    }
  };

  const renderSidebar = () => {
    const SidebarContent = () => (
      <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, ...styles.text.primary }}>
            Wedding Details
          </Typography>
          {isMobile && (
            <IconButton onClick={() => setMobileDrawerOpen(false)} size="small">
              <X size={20} />
            </IconButton>
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            return (
              <Button
                key={section.id}
                onClick={() => handleSectionChange(section.id)}
                variant={isActive ? 'contained' : 'outlined'}
                startIcon={<Icon size={20} weight={isActive ? 'fill' : 'regular'} />}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  fontWeight: isActive ? 600 : 400,
                  ...(isActive ? styles.button.primary : {}),
                  ...(!isActive ? {
                    bgcolor: 'transparent',
                    borderColor: 'divider',
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      borderColor: 'primary.main'
                    }
                  } : {})
                }}
                fullWidth
              >
                {section.label}
              </Button>
            );
          })}
        </Box>
      </Box>
    );

    if (isMobile) {
      return (
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          PaperProps={{
            sx: {
              width: 280,
              ...styles.card
            }
          }}
        >
          <SidebarContent />
        </Drawer>
      );
    }

    return (
      <Card sx={{ height: 'fit-content', position: 'sticky', top: 20, ...styles.card }}>
        <SidebarContent />
      </Card>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/supplier/crm')}>
          Back to CRM
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                bgcolor: 'action.hover'
              }
            }}
            aria-label="Go back"
          >
            <ArrowLeft size={24} />
          </IconButton>
          <Box>
            <Typography component="h2" className="pagename" sx={styles.text.pagename}>
              {weddingData?.wedding?.name || 'Wedding Details'}
            </Typography>
          {weddingData?.wedding?.weddingDate && (
            <Typography variant="body2" sx={{ ...styles.text.secondary }}>
              {new Date(weddingData.wedding.weddingDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
          )}
          </Box>
        </Box>
        {isMobile && (
          <IconButton onClick={() => setMobileDrawerOpen(true)}>
            <CalendarBlank size={24} />
          </IconButton>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Sidebar - 25% width on desktop */}
        <Grid item xs={12} md={3}>
          {renderSidebar()}
        </Grid>

        {/* Content Area - 75% width on desktop */}
        <Grid item xs={12} md={9}>
          <Card sx={{ ...styles.card }}>
            <CardContent sx={{ p: 3 }}>
              {isRedacted && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Information Restricted
                  </Typography>
                  <Typography variant="body2">
                    Wedding information is currently restricted. Once the couple adds you as a supplier in their dashboard, full details will be available.
                  </Typography>
                </Alert>
              )}
              {renderContent()}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default WeddingDetailsMasterPage;

